/** @format */
import { TFunction, useTranslation } from 'next-i18next';

import { ProductConditionStarRating } from '@toucan/common/services/search/constants';

import {
    centToDecimal,
    formatAsCurrency,
} from '@toucan/client/global/currency';
import { Market } from '@toucan/client/global/market';

import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';
import {
    Filter,
    FilterOption,
    PreTranslatedFilter,
} from '@toucan/client/features/search/components/model-search-results/types';
import { PriceFilterOption } from '@toucan/client/features/search/components/price-options/constants';
import { transformFacetsOptionValue } from '@toucan/client/features/search/helpers';

export function getNumberFilterOptions(
    filter: PreTranslatedFilter,
    activeMarket: Market,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: ReturnType<typeof useTranslation>['t']
): FilterOption[] {
    return filter.values.map((value) => {
        const { fieldValue } = value;

        const labelValue = fieldValue
            .toString()
            .replace(/[\d.]+/g, (value: string) =>
                Intl.NumberFormat(activeMarket.slug).format(parseFloat(value))
            );

        const label = transformFacetsOptionValue({
            value: labelValue,
            t,
        });

        return {
            label,
            value: value.fieldValue,
            quantity: parseInt(value.count),
        };
    });
}

export function getProductPriceFilterOptions(
    filter: PreTranslatedFilter,
    activeMarket: Market,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    searchParams: ReturnType<typeof useSearchParams>
): Filter {
    const formatPrice = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    function formatLabel(value: string) {
        const transformedValue = transformFacetsOptionValue({
            value,
            t,
        });

        const label = transformedValue.replace(/\d+/g, (match: string) => {
            return formatPrice(centToDecimal(parseInt(match)), {
                trailingZeros: false,
            });
        });

        return label;
    }

    const { selectedFilters, variants } = searchParams;
    const { productPrice } = variants;

    const filterQueryValue = selectedFilters[filter.id];

    const additionalValues =
        productPrice === PriceFilterOption.RANGE && filterQueryValue
            ? [
                  {
                      value: filterQueryValue.toString(),
                      label: formatLabel(filterQueryValue.toString()),
                      variant: PriceFilterOption.RANGE,
                      quantity: 0,
                  },
              ]
            : [];

    const filterValues = filter.values.map((value) => {
        return {
            label: formatLabel(value.fieldValue),
            value: value.fieldValue,
            variant: PriceFilterOption.GROUP,
            quantity: parseInt(value.count),
        };
    });

    const values: FilterOption[] = [...filterValues, ...additionalValues];

    return {
        ...filter,
        values,
    };
}

export function getProductConditionFilterOptions(
    filter: PreTranslatedFilter,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: ReturnType<typeof useTranslation>['t']
): FilterOption[] {
    const options = {
        context: 'searchFacet',
    };
    const productConditionStarRatingMap = {
        [ProductConditionStarRating.ZERO]: t('Spares and repairs', options),
        [ProductConditionStarRating.ONE]: t('Heavily used', options),
        [ProductConditionStarRating.TWO]: t('Well used', options),
        [ProductConditionStarRating.THREE]: t('Good', options),
        [ProductConditionStarRating.FOUR]: t('Excellent', options),
        [ProductConditionStarRating.FIVE]: t('Like new', options),
        [ProductConditionStarRating.SIX]: t('Brand new', options),
    };

    return filter.values.map(({ fieldValue, count }) => {
        const value = fieldValue as ProductConditionStarRating;

        return {
            label: productConditionStarRatingMap[value],
            value: fieldValue,
            quantity: parseInt(count),
        };
    });
}

export function getYearOfReleaseFilterOptions(
    filter: PreTranslatedFilter,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: ReturnType<typeof useTranslation>['t']
): FilterOption[] {
    return filter.values.map(({ fieldValue, count }) => {
        const beforeLanguage = (value: string) =>
            t('Before {{value}}', {
                value,
                context: 'search-filters',
            });
        const afterLanguage = (value: string) =>
            t('After {{value}}', {
                value,
                context: 'search-filters',
            });

        const label = transformFacetsOptionValue({
            value: fieldValue,
            bottomBoundaryLanguage: beforeLanguage,
            topBoundaryLanguage: afterLanguage,
            t,
        });

        return {
            label,
            value: fieldValue,
            quantity: parseInt(count),
        };
    });
}

export function getDefaultFilterOptions(
    filter: PreTranslatedFilter,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: ReturnType<typeof useTranslation>['t']
): FilterOption[] {
    function getFilterOptionLabel(fieldValue: string) {
        if (fieldValue === 'true') {
            return t('Yes');
        }

        if (fieldValue === 'false') {
            return t('No');
        }

        return fieldValue.replace('-', ' - ');
    }

    return filter.values.map(({ fieldValue, count }) => {
        return {
            label: getFilterOptionLabel(fieldValue) ?? fieldValue,
            value: fieldValue,
            quantity: parseInt(count),
        };
    });
}
